import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { Seo } from '../../components/Seo';
import { MapPin, Award, BadgeCheck, Phone, Globe, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const toEmbed = (url) => {
  if (!url) return '';
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return url;
};

const STATE_NAMES = { AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',CT:'Connecticut',DE:'Delaware',DC:'District of Columbia',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming' };

export const AuctioneerProfile = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [form, setForm] = useState({ sender_name: '', sender_email: '', sender_phone: '', message: '', company: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [contactError, setContactError] = useState('');

  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true); setContactError('');
    try {
      const res = await fetch(`${API}/api/directory/auctioneer/${slug}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not send your message.');
      }
      setSent(true);
    } catch (err) {
      setContactError(err.message);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/directory/auctioneer/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error('nf');
        return r.json();
      })
      .then((d) => setData(d))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (notFound) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center text-gray-600" data-testid="auctioneer-not-found">
          Auctioneer not found.
        </div>
        <Footer />
      </>
    );
  }
  if (loading || !data) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>
        <Footer />
      </>
    );
  }

  const a = data.auctioneer;
  const displayName = a.business_name || a.name;
  const stateName = STATE_NAMES[a.state] || a.state;
  const stateSlug = (a.state || '').toLowerCase();
  const citySlug = (a.city || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const bioText = a.bio || `${displayName} is a licensed auctioneer based in ${a.city}, ${stateName}. Contact them for estate sales, real estate, and personal property auctions.`;

  const localBusiness = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: displayName,
    description: bioText,
    url: `https://netauctionsinc.com/auctioneer/${a.slug}`,
    address: {
      '@type': 'PostalAddress',
      addressLocality: a.city,
      addressRegion: a.state,
      addressCountry: 'US',
    },
    ...(a.phone ? { telephone: a.phone } : {}),
    ...(a.website ? { sameAs: [a.website] } : {}),
  };
  const faq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: `What does ${displayName} specialize in?`, acceptedAnswer: { '@type': 'Answer', text: (a.specialties && a.specialties.length ? a.specialties.join(', ') : 'General auction services including estate sales and personal property.') } },
      { '@type': 'Question', name: `Where is ${displayName} located?`, acceptedAnswer: { '@type': 'Answer', text: `${displayName} is based in ${a.city}, ${stateName} and serves the surrounding area.` } },
      { '@type': 'Question', name: `How do I book ${displayName}?`, acceptedAnswer: { '@type': 'Answer', text: `Request a free quote through Net Auctions or contact ${displayName} directly by phone or website.` } },
    ],
  };
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Find an Auctioneer', item: 'https://netauctionsinc.com/find-an-auctioneer' },
      { '@type': 'ListItem', position: 2, name: stateName, item: `https://netauctionsinc.com/find-an-auctioneer/${stateSlug}` },
      { '@type': 'ListItem', position: 3, name: a.city, item: `https://netauctionsinc.com/find-an-auctioneer/${stateSlug}/${citySlug}` },
      { '@type': 'ListItem', position: 4, name: a.name, item: `https://netauctionsinc.com/auctioneer/${a.slug}` },
    ],
  };

  return (
    <>
      <Navbar />
      <Seo
        title={`${displayName} — ${a.city}, ${a.state} Auctioneer | Net Auctions`}
        description={`${displayName} is a licensed auctioneer in ${a.city}, ${stateName}. ${bioText.slice(0, 140)}`}
        canonicalPath={`/auctioneer/${a.slug}`}
        jsonLd={[localBusiness, faq, breadcrumb]}
      />
      <div className="min-h-screen bg-gray-50" data-testid="auctioneer-profile">
        {contactOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="contact-modal" onClick={() => setContactOpen(false)}>
            <div className="bg-white rounded-2xl w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setContactOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" data-testid="contact-close" aria-label="Close">✕</button>
              {sent ? (
                <div className="text-center py-6" data-testid="contact-success">
                  <div className="text-green-600 text-4xl mb-3">✓</div>
                  <h3 className="text-lg font-bold text-gray-900">Message sent!</h3>
                  <p className="text-gray-600 mt-1">{displayName} will get your inquiry by email. Watch for their reply.</p>
                  <button onClick={() => setContactOpen(false)} className="mt-5 px-5 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700">Done</button>
                </div>
              ) : (
                <form onSubmit={submitContact} data-testid="contact-form">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Contact {displayName}</h3>
                  <p className="text-sm text-gray-500 mb-4">Your message is delivered securely — their email stays private.</p>
                  {contactError && <div className="mb-3 text-sm text-red-600 bg-red-50 rounded p-2" data-testid="contact-error">{contactError}</div>}
                  <input type="text" required placeholder="Your name" data-testid="contact-name"
                    className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                    value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} />
                  <input type="email" required placeholder="Your email" data-testid="contact-email"
                    className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                    value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} />
                  <input type="tel" placeholder="Your phone (optional)" data-testid="contact-phone"
                    className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                    value={form.sender_phone} onChange={(e) => setForm({ ...form, sender_phone: e.target.value })} />
                  <textarea required rows={4} placeholder="Tell them about your auction or event…" data-testid="contact-message"
                    className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                    value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                  {/* honeypot */}
                  <input type="text" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true"
                    value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                  <button type="submit" disabled={sending} data-testid="contact-submit"
                    className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50">
                    {sending ? 'Sending…' : 'Send message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}
        <div className="bg-gradient-to-r from-green-700 to-green-800 text-white py-14">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-green-200 text-sm mb-3">
              <Link to="/find-an-auctioneer" className="hover:text-white">Find an Auctioneer</Link>
              <span className="mx-2">/</span>
              <Link to={`/find-an-auctioneer/${stateSlug}`} className="hover:text-white">{stateName}</Link>
              <span className="mx-2">/</span>
              <Link to={`/find-an-auctioneer/${stateSlug}/${citySlug}`} className="hover:text-white">{a.city}</Link>
            </nav>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-4xl sm:text-5xl font-bold">{displayName}</h1>
              {a.is_premier && (
                <span className="inline-flex items-center gap-1 text-sm font-bold bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full">
                  <Award className="w-4 h-4" /> Premier
                </span>
              )}
              {a.verified && (
                <span className="inline-flex items-center gap-1 text-sm font-medium bg-green-100 text-green-800 px-3 py-1 rounded-full">
                  <BadgeCheck className="w-4 h-4" /> Verified
                </span>
              )}
            </div>
            <p className="text-lg text-green-100 flex items-center gap-1 mt-2">
              <MapPin className="w-5 h-5" /> {a.city}, {stateName}
            </p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <section className="bg-white rounded-xl p-6 border border-gray-200">
              <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-3">About {displayName}</h2>
              <p className="text-gray-700 leading-relaxed">{bioText}</p>
              {a.years_experience && (
                <p className="text-sm text-gray-500 mt-3">💼 {a.years_experience}+ years of experience</p>
              )}
            </section>

            {a.video_url && (
              <section className="bg-white rounded-xl p-6 border border-gray-200" data-testid="profile-video">
                <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-3">Video</h2>
                <div className="relative w-full rounded-lg overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                  <iframe src={toEmbed(a.video_url)} title="Auctioneer video" allowFullScreen
                    className="absolute inset-0 w-full h-full" frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
                </div>
              </section>
            )}

            {a.gallery_images?.length > 0 && (
              <section className="bg-white rounded-xl p-6 border border-gray-200" data-testid="profile-gallery">
                <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-3">Gallery</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {a.gallery_images.map((u, i) => (
                    <a key={i} href={`${API}${u}`} target="_blank" rel="noopener noreferrer"
                      className="block rounded-lg overflow-hidden border border-gray-200 aspect-square">
                      <img src={`${API}${u}`} alt={`${displayName} gallery ${i + 1}`} loading="lazy"
                        className="w-full h-full object-cover hover:scale-105 transition-transform" />
                    </a>
                  ))}
                </div>
              </section>
            )}

            {a.specialties?.length > 0 && (
              <section className="bg-white rounded-xl p-6 border border-gray-200">
                <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-3">Specialties</h2>
                <div className="flex flex-wrap gap-2">
                  {a.specialties.map((s, i) => (
                    <span key={i} className="px-3 py-1 bg-green-50 text-green-800 text-sm rounded-full">{s}</span>
                  ))}
                </div>
              </section>
            )}

            {a.certifications?.length > 0 && (
              <section className="bg-white rounded-xl p-6 border border-gray-200">
                <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-3">Certifications & Licensing</h2>
                <ul className="space-y-2">
                  {a.certifications.map((c, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-700">
                      <BadgeCheck className="w-4 h-4 text-green-600" /> {c}
                    </li>
                  ))}
                  {a.license_number && (
                    <li className="text-sm text-gray-500">License: {a.license_number} ({a.license_state || a.state})</li>
                  )}
                </ul>
              </section>
            )}

            {data.related?.length > 0 && (
              <section>
                <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-3">Other Auctioneers in {a.city}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {data.related.map((r) => (
                    <Link key={r.id} to={`/auctioneer/${r.slug}`} className="block bg-white rounded-lg p-4 border border-gray-200 hover:border-green-500 transition-colors" data-testid={`related-${r.slug}`}>
                      <p className="font-semibold text-gray-900">{r.name}</p>
                      <p className="text-sm text-gray-500">{r.city}, {r.state}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 border border-gray-200 sticky top-6" data-testid="contact-card">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Contact {displayName}</h2>
              <button
                onClick={() => { setContactOpen(true); setSent(false); setContactError(''); }}
                data-testid="request-quote-btn"
                className="block w-full text-center px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors mb-3"
              >
                Contact {displayName.length > 22 ? 'this auctioneer' : displayName}
              </button>
              {a.phone && (
                <a href={`tel:${a.phone}`} className="flex items-center gap-2 text-gray-700 py-2 hover:text-green-700" data-testid="profile-phone">
                  <Phone className="w-4 h-4" /> {a.phone}
                </a>
              )}
              {a.website && (
                <a href={a.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-700 py-2 hover:text-green-700" data-testid="profile-website">
                  <Globe className="w-4 h-4" /> Visit website
                </a>
              )}
              {data.contactable && (
                <p className="flex items-center gap-2 text-gray-400 text-sm py-2" data-testid="profile-email-protected">
                  <Mail className="w-4 h-4" /> Email protected — use the contact button
                </p>
              )}
              {!a.claimed && (
                <Link
                  to={`/auctioneer/individual/claim?id=${a.id}`}
                  className="block w-full text-center px-4 py-2 mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                  data-testid="claim-listing-btn"
                >
                  Is this you? Claim this listing
                </Link>
              )}
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </>
  );
};
